import React, { useRef, useEffect, useState, useCallback } from "react";
import { Tooltip } from "src/assets/imports/antdComponents";
import type { TooltipPlacement } from "antd/es/tooltip";

interface EllipsisTooltipProps {
	title: string;
	children: React.ReactNode;
	className?: string;
	placement?: TooltipPlacement;
	maxWidth?: number | string;
}

const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({ title, children, className = "", placement = "top", maxWidth }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	const checkOverflow = useCallback(() => {
		const element = containerRef.current;
		if (element) {
			const isOverflow = element.scrollWidth > element.clientWidth;
			setIsOverflowing(isOverflow);
		}
	}, []);

	useEffect(() => {
		const element = containerRef.current;

		if (!element) return;
		checkOverflow();

		const resizeObserver = new ResizeObserver(() => {
			checkOverflow();
		});

		resizeObserver.observe(element);
		window.addEventListener("resize", checkOverflow);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", checkOverflow);
		};
	}, [checkOverflow]);

	useEffect(() => {
		checkOverflow();
	}, [title, checkOverflow]);

	const content = (
		<div
			ref={containerRef}
			className={`overflow-hidden text-ellipsis whitespace-nowrap ${className}`}
			style={maxWidth ? { maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth } : undefined}>
			{children}
		</div>
	);

	if (isOverflowing) {
		return (
			<Tooltip title={title} placement={placement} overlayClassName="custom-tooltip">
				{content}
			</Tooltip>
		);
	}

	return content;
};

export default EllipsisTooltip;
